// web/src/tutorial/render.ts
// Block[] → HTML。内容里的行内标记：
//   `按钮名`  → <kbd class="ui-ref">按钮名</kbd>（界面上的按钮/参数名）
//   **强调**  → <strong>
// 其他文本一律转义。

import type { Lang } from "../i18n";
import { pickText } from "../site-data";
import type {
  Block,
  CalloutTone,
  ImageRef,
  LocalizedText,
  Section,
  SubSection,
  TutorialDoc,
} from "./types";

function normalizeTutorialCopy(rawText: string, lang: Lang): string {
  let normalizedText = rawText.replace(/`/g, "");
  if (lang === "zh") {
    normalizedText = normalizedText
      .replace(/这一页做什么/g, "这一页主要做什么")
      .replace(/每次出片都会用到/g, "每次生成动画都会用到")
      .replace(/只检查、不改动/g, "只检查，不会改动")
      .replace(/这是一份\*\*参数查询手册\*\*/g, "这页可以当作**参数速查手册**")
      .replace(/按截图逐屏拆开讲/g, "按截图一屏一屏讲清楚")
      .replace(/这一屏决定/g, "这一屏主要决定")
      .replace(/才是平时真正干活的地方/g, "是你日常最常用的部分")
      .replace(/才是日常干活的地方/g, "是你日常最常用的部分")
      .replace(/每次出片都要用的地方/g, "每次生成动画都会用到")
      .replace(/只做一次性的准备/g, "主要用于一次性初始化")
      .replace(/一列数字格子/g, "一列数字输入框")
      .replace(/窗口里出现的两个页签。/g, "窗口里会出现两个核心页签。")
      .replace(
        /两张界面截图都在右下角的悬浮窗口里，会跟着你读到的小节自动切换；也可以点窗口上的/g,
        "右下角有一个悬浮截图窗，会跟着当前阅读小节自动切换；你也可以直接点窗口上的",
      )
      .replace(
        /两张界面截图都在右下角的悬浮窗口里，滚到哪一节窗口就自动换成那一张，也可以点窗口上的/g,
        "右下角的悬浮截图窗会随滚动自动切换到对应页面；也可以点窗口上的",
      )
      .replace(
        /两张界面截图都在右下角的悬浮窗口里，滚到哪一节，窗口就自动换成对应的那张；也可以点窗口上的/g,
        "右下角的悬浮截图窗会跟随你阅读的章节自动切换；也可以点窗口上的",
      )
      .replace(
        /选中(.+?)之后，窗口里出现的两个页签。/g,
        "选中$1后，窗口会出现两个核心页签。",
      );
  }
  return normalizedText;
}

export function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );
}

/**
 * 行内格式化：先转义，再处理 `代码`、**强调** 与 [文字](站内路径) 链接。
 * 链接只允许站内路径或 https，避免内容里混进奇怪协议。
 */
export function inline(text: string): string {
  return escapeHtml(text)
    .replace(/`([^`]+)`/g, '<kbd class="ui-ref">$1</kbd>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(
      /\[([^\]]+)\]\((\/[^()\s]*|https:\/\/[^()\s]+)\)/g,
      '<a href="$2">$1</a>',
    );
}

function t(text: LocalizedText, lang: Lang): string {
  return inline(normalizeTutorialCopy(pickText(text, lang), lang));
}

function ratioClass(ratio?: string): string {
  switch (ratio) {
    case "4-3":
      return "ratio-4-3";
    case "3-2":
      return "ratio-3-2";
    case "1-1":
      return "ratio-1-1";
    default:
      return "ratio-16-9";
  }
}

const CALLOUT_LABEL: Record<CalloutTone, { zh: string; en: string }> = {
  info: { zh: "说明", en: "Note" },
  tip: { zh: "建议", en: "Tip" },
  warn: { zh: "注意", en: "Warning" },
};

export function renderBlock(block: Block, lang: Lang): string {
  switch (block.kind) {
    case "p":
      return `<p class="tut-p">${t(block.text, lang)}</p>`;

    case "ul":
    case "ol": {
      const tag = block.kind;
      const items = block.items.map((i) => `<li>${t(i, lang)}</li>`).join("");
      return `<${tag} class="tut-list">${items}</${tag}>`;
    }

    case "table": {
      const head = block.head.map((h) => `<th>${t(h, lang)}</th>`).join("");
      const rows = block.rows
        .map(
          (r) => `<tr>${r.map((c) => `<td>${t(c, lang)}</td>`).join("")}</tr>`,
        )
        .join("");
      // cols-N：手机上用来决定「这张表是收窄换行，还是保留横向滚动」，
      // 列数多（>=4）的表硬压进 360px 会把每列挤成几个字。
      return `<div class="tut-table-wrap"><table class="tut-table cols-${block.head.length}"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table></div>`;
    }

    case "figure": {
      const alt = pickText(block.alt, lang);
      const caption = t(block.caption, lang);
      // 中英两版插图不同时用 srcEn（图里的文字是画进去的），缺省两侧共用 src
      const src = lang === "en" && block.srcEn ? block.srcEn : block.src;
      if (src) {
        return `<figure class="tut-figure">
  <img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="lazy" decoding="async" />
  <figcaption>${caption}</figcaption>
</figure>`;
      }
      const badge = block.badge
        ? t(block.badge, lang)
        : lang === "zh"
          ? "截图待补"
          : "Screenshot pending";
      return `<figure class="tut-figure is-placeholder" title="${escapeHtml(alt)}">
  <div class="media-placeholder ${ratioClass(block.ratio)}">
    <span class="tut-ph-badge">${badge}</span>
    <span class="tut-ph-text">${caption}</span>
  </div>
</figure>`;
    }

    case "pre": {
      // 示意图：只转义，不解析任何行内标记（反引号 / ** 都是图的一部分）
      const caption = block.caption
        ? `<figcaption>${t(block.caption, lang)}</figcaption>`
        : "";
      return `<figure class="tut-diagram">
<pre class="tut-pre">${escapeHtml(pickText(block.text, lang))}</pre>${caption}
</figure>`;
    }

    case "callout": {
      const label = CALLOUT_LABEL[block.tone][lang];
      const title = block.title ? t(block.title, lang) : label;
      return `<aside class="tut-callout tone-${block.tone}">
  <span class="tut-callout-tag">${title}</span>
  <p>${t(block.text, lang)}</p>
</aside>`;
    }

    case "steps": {
      const items = block.items
        .map(
          (s, i) => `<li class="tut-step">
  <span class="tut-step-no">${i + 1}</span>
  <div class="tut-step-body"><strong>${t(s.title, lang)}</strong><p>${t(s.text, lang)}</p></div>
</li>`,
        )
        .join("");
      return `<ol class="tut-steps">${items}</ol>`;
    }

    case "note":
      return `<p class="tut-note">${t(block.text, lang)}</p>`;
  }
}

function renderBlocks(blocks: Block[] | undefined, lang: Lang): string {
  if (!blocks || blocks.length === 0) return "";
  return blocks.map((b) => renderBlock(b, lang)).join("\n");
}

/**
 * 区块 → 截图的关联写成 data-shot（只带图片下标）。
 * 右下角悬浮窗读到它就会在滚到该区块时切换成对应截图；整张图不裁剪，所以没有别的属性。
 */
function shotAttr(ref: ImageRef | undefined): string {
  if (!ref || ref.image === undefined || ref.image === "") return "";
  return ` data-shot="${escapeHtml(String(ref.image))}"`;
}

function renderSub(sub: SubSection, lang: Lang): string {
  const subTitle = normalizeTutorialCopy(pickText(sub.title, lang), lang);
  return `<section class="tut-subsection"${shotAttr(sub.image)}>
<h3 id="${escapeHtml(sub.id)}">${escapeHtml(subTitle)}</h3>
${renderBlocks(sub.blocks, lang)}
</section>`;
}

export function renderSections(sections: Section[], lang: Lang): string {
  return sections
    .map(
      (s) => `<section class="tut-section"${shotAttr(s.image)}>
<h2 id="${escapeHtml(s.id)}">${escapeHtml(normalizeTutorialCopy(pickText(s.title, lang), lang))}</h2>
${renderBlocks(s.blocks, lang)}
${(s.subs || []).map((x) => renderSub(x, lang)).join("\n")}
</section>`,
    )
    .join("\n");
}

/** 由 B 站视频页地址推导播放器嵌入地址；无法识别时返回 null */
export function bilibiliEmbed(url?: string): string | null {
  if (!url) return null;
  const m = url.match(/(BV[0-9A-Za-z]+)/);
  if (!m) return null;
  return `//player.bilibili.com/player.html?isOutside=true&poster=1&autoplay=0&bvid=${m[1]}&p=1`;
}

/** 由 YouTube 视频页地址（或裸 videoId）推导嵌入地址；无法识别时返回 null */
export function youtubeEmbed(url?: string): string | null {
  if (!url) return null;
  const m =
    url.match(/(?:[?&]v=|youtu\.be\/|embed\/|\/shorts\/)([A-Za-z0-9_-]{6,})/) ||
    url.match(/^([A-Za-z0-9_-]{6,})$/);
  if (!m) return null;
  return `https://www.youtube.com/embed/${m[1]}?rel=0`;
}

export function docIcon(doc: TutorialDoc, lang: Lang): string {
  return `${doc.icon} ${normalizeTutorialCopy(pickText(doc.title, lang), lang)}`;
}
